import { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import JobStatusStepper from "../../admin/Projects/ProjectDetails/Jobs/JobDetails/JobStatusStepper";
import VendorUploadOutputFileModal from "./VendorUploadOutputFileModal";
import BackButton from "../../../components/Button/BackButton";
import { AuthContext } from "../../../context/AuthContext";
import { toast, confirmDialog } from "../../../components/Toast/toast";
import { btn } from "../../../components/Button/buttonStyles";
import { adjustmentLabel } from "../../../utils/adjustments";

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtSize = (bytes) => {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(2)} KB` : `${(kb / 1024).toFixed(2)} MB`;
};

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "—";

const STATUS_BADGE = {
  Draft: "bg-gray-100 text-gray-700",
  "Offered to Vendor": "bg-purple-100 text-purple-700",
  "Offer Accepted": "bg-green-100 text-green-700",
  "Offer Rejected": "bg-danger-100 text-danger-700",
  Started: "bg-brand-100 text-brand-700",
  Completed: "bg-teal-100 text-teal-700",
  Hold: "bg-warning-100 text-warning-700",
  "Completion Accepted": "bg-green-100 text-green-700",
  "Completion Rejected": "bg-danger-100 text-danger-700",
  Cancelled: "bg-gray-100 text-gray-700",
};

const Row = ({ label, value, children }) => (
  <div className="flex border-b last:border-b-0 text-sm">
    <div className="w-1/3 bg-gray-50 px-4 py-3 font-medium text-gray-700">
      {label}
    </div>
    <div className="w-2/3 px-4 py-3 text-gray-900">
      {children ?? value ?? "—"}
    </div>
  </div>
);

const SectionGroup = ({ title, children }) => (
  <div className="space-y-2">
    <h3 className="text-base font-semibold text-gray-800">{title}</h3>
    <div className="border border-gray-200 rounded-md overflow-hidden">
      {children}
    </div>
  </div>
);

const TableCard = ({ title, count, action, headers, minWidth, children }) => (
  <div className="bg-white shadow rounded-lg overflow-hidden">
    <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100">
      <h3 className="text-base font-semibold text-gray-800">
        {title}
        {count !== undefined && (
          <span className="ml-2 text-gray-500 font-medium">({count})</span>
        )}
      </h3>
      {action}
    </div>
    <div className="overflow-x-auto">
      <table
        className="w-full table-auto border-collapse"
        style={minWidth ? { minWidth } : undefined}
      >
        <thead className="bg-white border-b">
          <tr className="text-[10px] tracking-widest text-gray-500 uppercase">
            {headers.map((h) => (
              <th
                key={h}
                className="px-3 py-2 text-center font-semibold whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  </div>
);

const ReceivablesCard = ({ data = [] }) => {
  const HEADERS = [
    "Type", "Code", "Invoice", "Unit amount", "Unit", "Price per unit",
    "Subtotal", "Extra charge", "Discount", "Total", "Currency",
    "Note for vendor", "Filename",
  ];

  return (
    <TableCard title="Receivables" count={data.length} headers={HEADERS} minWidth="1100px">
      {data.length === 0 ? (
        <tr>
          <td
            colSpan={HEADERS.length}
            className="py-10 text-center text-gray-400 text-xs"
          >
            No receivables found.
          </td>
        </tr>
      ) : (
        data.map((row, i) => {
          const isFlat = row.type === "flat_rate";
          const currCode = row.currency?.currency?.code || "—";
          const rowBg = i % 2 === 0 ? "bg-gray-50" : "bg-white";
          return (
            <tr
              key={`${row.type}-${row.id}`}
              className={`${rowBg} hover:bg-brand-50 transition-colors`}
            >
              <td className="px-3 py-2 text-xs text-center whitespace-nowrap">
                <span
                  className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                    isFlat
                      ? "bg-amber-100 text-amber-700"
                      : "bg-teal-100 text-teal-700"
                  }`}
                >
                  {isFlat ? "Flat rate" : "Unit based"}
                </span>
              </td>
              <td className="px-3 py-2 text-xs text-center text-gray-700">—</td>
              <td className="px-3 py-2 text-xs text-center text-gray-700">—</td>
              <td className="px-3 py-2 text-xs text-right text-gray-700">
                {isFlat ? "—" : fmtNum(row.unit_amount)}
              </td>
              <td className="px-3 py-2 text-xs text-center text-gray-700">
                {isFlat ? "—" : row.unit?.name || "—"}
              </td>
              <td className="px-3 py-2 text-xs text-right text-gray-700">
                {isFlat ? "—" : fmtNum(row.price_per_unit)}
              </td>
              <td className="px-3 py-2 text-xs text-right text-gray-700">
                {fmtNum(row.subtotal)}
              </td>
              <td className="px-3 py-2 text-xs text-right text-gray-700">{adjustmentLabel(row, "extra_charge")}</td>
              <td className="px-3 py-2 text-xs text-right text-gray-700">{adjustmentLabel(row, "discount")}</td>
              <td className="px-3 py-2 text-xs text-right font-semibold text-gray-900">
                {fmtNum(row.total ?? row.subtotal)}
              </td>
              <td className="px-3 py-2 text-xs text-center text-gray-700">
                {currCode}
              </td>
              <td className="px-3 py-2 text-xs text-gray-700">
                {row.note_for_vendor || "—"}
              </td>
              <td className="px-3 py-2 text-xs text-brand-600">
                {row.file?.original_file_name || row.file?.file_name || "—"}
              </td>
            </tr>
          );
        })
      )}
    </TableCard>
  );
};

const InputFilesCard = ({
  files = [],
  downloading,
  onDownloadZip,
  onDownloadSingle,
}) => {
  const HEADERS = [
    "File #", "Filename", "Size", "Category", "Note",
    "Uploaded at", "Uploaded by", "Output from job",
  ];

  return (
    <TableCard
      title="Linked Input Files"
      count={files.length}
      headers={HEADERS}
      action={
        <button
          onClick={onDownloadZip}
          disabled={downloading || files.length === 0}
          className={btn("primary", "sm")}
        >
          {downloading ? "Downloading..." : "Download as zip"}
        </button>
      }
    >
      {files.length === 0 ? (
        <tr>
          <td
            colSpan={HEADERS.length}
            className="py-10 text-center text-gray-400 text-xs"
          >
            No input files linked.
          </td>
        </tr>
      ) : (
        files.map((file, idx) => {
          const fileName = file.is_linked
            ? file.linkedProjectFile?.original_file_name || "—"
            : file.original_file_name || "—";
          const fileSize = fmtSize(
            file.is_linked
              ? file.linkedProjectFile?.file_size
              : file.file_size
          );
          const rowBg = idx % 2 === 0 ? "bg-gray-50" : "bg-white";
          return (
            <tr
              key={file.id || idx}
              className={`${rowBg} hover:bg-brand-50 transition-colors`}
            >
              <td className="px-3 py-2 text-xs text-center text-gray-700">
                {file.file_code || `#${idx + 1}`}
              </td>
              <td className="px-3 py-2 text-xs text-center">
                <button
                  onClick={() => onDownloadSingle(file, "input")}
                  className="text-brand-600 hover:text-brand-800 underline font-medium"
                >
                  {fileName}
                </button>
              </td>
              <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                {fileSize}
              </td>
              <td className="px-3 py-2 text-xs text-center text-gray-700">
                {file.category || "—"}
              </td>
              <td className="px-3 py-2 text-xs text-gray-600">
                {file.note || "—"}
              </td>
              <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-500">
                {fmtDate(file.uploaded_at)}
              </td>
              <td className="px-3 py-2 text-xs text-center text-gray-700">
                {file.uploaded_by || "—"}
              </td>
              <td className="px-3 py-2 text-xs text-center text-gray-700">
                {file.output_from_job || "—"}
              </td>
            </tr>
          );
        })
      )}
    </TableCard>
  );
};

const OutputFilesCard = ({
  files = [],
  downloading,
  onDownloadZip,
  onDownloadSingle,
  onUpload,
  status,
}) => {
  const HEADERS = [
    "File #", "Filename", "Size", "Uploaded at", "Uploaded by",
    "Input for jobs", "Project output",
  ];

  return (
    <TableCard
      title="Output Files"
      count={files.length}
      headers={HEADERS}
      action={
        <div className="flex gap-2">
          {status === "Started" && (
            <button
              onClick={onUpload}
              className={btn("primary", "sm")}
            >
              Upload
            </button>
          )}
          <button
            onClick={onDownloadZip}
            disabled={downloading || files.length === 0}
            className={btn("primary", "sm")}
          >
            {downloading ? "Downloading..." : "Download as zip"}
          </button>
        </div>
      }
    >
      {files.length === 0 ? (
        <tr>
          <td
            colSpan={HEADERS.length}
            className="py-10 text-center text-gray-400 text-xs"
          >
            No output files found.
          </td>
        </tr>
      ) : (
        files.map((file, idx) => {
          let uploader = "—";
          if (file.adminUploader?.details) {
            uploader = `${file.adminUploader.details.first_name} ${file.adminUploader.details.last_name}`;
          } else if (file.vendorUploader?.primary_users) {
            uploader = `${file.vendorUploader.primary_users.first_name} ${file.vendorUploader.primary_users.last_name}`;
          }
          const rowBg = idx % 2 === 0 ? "bg-gray-50" : "bg-white";
          return (
            <tr
              key={file.id || idx}
              className={`${rowBg} hover:bg-brand-50 transition-colors`}
            >
              <td className="px-3 py-2 text-xs text-center text-gray-700">
                {file.file_code || `#${idx + 1}`}
              </td>
              <td className="px-3 py-2 text-xs text-center">
                <button
                  onClick={() => onDownloadSingle(file, "output")}
                  className="text-brand-600 hover:text-brand-800 underline font-medium"
                >
                  {file.original_file_name || "—"}
                </button>
              </td>
              <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                {fmtSize(file.file_size)}
              </td>
              <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-500">
                {fmtDate(file.uploaded_at)}
              </td>
              <td className="px-3 py-2 text-xs text-center text-gray-700">
                {uploader}
              </td>
              <td className="px-3 py-2 text-xs text-center text-gray-700">
                {file.input_for_job || "—"}
              </td>
              <td className="px-3 py-2 text-xs text-center">
                {file.is_project_output ? (
                  <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                    ✓ Yes
                  </span>
                ) : (
                  <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                    No
                  </span>
                )}
              </td>
            </tr>
          );
        })
      )}
    </TableCard>
  );
};

const VendorJobDetailPage = () => {
  const { jobId } = useParams();
  const { user } = useContext(AuthContext);
  const financesVisible = user?.vendor_flags?.finances_visible ?? false;

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [inputDownloading, setInputDownloading] = useState(false);
  const [outputDownloading, setOutputDownloading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const fetchJob = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/vendor-jobs/${jobId}`, {
        withCredentials: true,
      });
      setJob(data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load job details");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) fetchJob();
  }, [jobId, fetchJob]);

  const handleAcceptOffer = async () => {
    setActionLoading(true);
    try {
      await api.post(
        `/vendor-jobs/${jobId}/accept`,
        {},
        { withCredentials: true }
      );
      await fetchJob();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to accept offer");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectOffer = async () => {
    const comment = prompt("Enter reason for rejection (optional):");
    if (comment === null) return;
    setActionLoading(true);
    try {
      await api.post(
        `/vendor-jobs/${jobId}/reject`,
        { comment },
        { withCredentials: true }
      );
      await fetchJob();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reject offer");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadSingle = async (file, type) => {
    try {
      const filePath =
        type === "input"
          ? file.is_linked
            ? file.linkedProjectFile?.file_path
            : file.file_path
          : file.file_path;

      if (!filePath) {
        toast.error("File path not available");
        return;
      }

      const res = await api.get(
        `/download-file?path=${encodeURIComponent(filePath)}`,
        { responseType: "blob" }
      );
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        type === "input"
          ? (file.is_linked
              ? file.linkedProjectFile?.original_file_name
              : file.original_file_name) || "file"
          : file.original_file_name || "file";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to download file");
    }
  };

  const handleDownloadInputZip = async () => {
    try {
      setInputDownloading(true);
      const res = await api.get(
        `/vendor-jobs/${jobId}/download-input-zip`,
        { responseType: "blob" }
      );
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `job-${jobId}-input-files.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error("Failed to download zip");
    } finally {
      setInputDownloading(false);
    }
  };

  const handleDownloadOutputZip = async () => {
    try {
      setOutputDownloading(true);
      const res = await api.get(
        `/vendor-jobs/${jobId}/download-output-zip`,
        { responseType: "blob" }
      );
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `job-${jobId}-output-files.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error("Failed to download zip");
    } finally {
      setOutputDownloading(false);
    }
  };

  const handleCompleteJob = async () => {
    if (!await confirmDialog("Mark this job as completed?")) return;
    setActionLoading(true);
    try {
      await api.post(
        `/vendor-jobs/${jobId}/complete`,
        {},
        { withCredentials: true }
      );
      await fetchJob();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to complete job");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  }
  if (error) return <p className="text-danger-600 text-sm">{error}</p>;
  if (!job) return <p className="text-sm text-gray-500">Job not found.</p>;

  const langPair = job.languagePair
    ? `${job.languagePair.sourceLanguage?.name || ""} → ${
        job.languagePair.targetLanguage?.name || ""
      }`
    : "—";

  const primaryManager = job.project?.primaryManager
    ? `${job.project.primaryManager.first_name} ${job.project.primaryManager.last_name}`
    : "—";

  const deadlineDisplay = () => {
    if (!job.deadline_at) return "—";
    const now = new Date();
    const deadline = new Date(job.deadline_at);
    const diff = deadline - now;
    if (diff < 0) {
      const abs = Math.abs(diff);
      const days = Math.floor(abs / 86400000);
      const hours = Math.floor((abs % 86400000) / 3600000);
      return (
        <span className="text-danger-600 font-medium">
          Overdue by {days}d {hours}h ({fmtDate(job.deadline_at)})
        </span>
      );
    }
    return fmtDate(job.deadline_at);
  };

  const statusCls = STATUS_BADGE[job.status] || "bg-gray-100 text-gray-700";

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <BackButton />
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Job details</h1>
      </div>

      <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-5">
        <JobStatusStepper
          current={job.status}
          statusHistory={job.statusHistory || []}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-6 space-y-5">
          <div className="bg-white shadow rounded-lg p-6 space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                JOB LT{job.id}
              </h2>
              <button
                onClick={() => toast.error("PO download coming soon!")}
                className={btn("primary", "sm")}
              >
                Download PO
              </button>
            </div>

            <SectionGroup title="Requirements">
              <Row label="Name" value={job.name} />
              <Row label="Service" value={job.service?.name} />
              <Row label="Language pair" value={langPair} />
              <Row
                label="Project specialization"
                value={job.project?.specialization?.name}
              />
              <Row label="Deadline at" value={fmtDate(job.deadline_at)} />
            </SectionGroup>

            <SectionGroup title="Setup">
              <Row
                label="Auto start on vendor acceptance"
                value={job.auto_start_on_vendor_acceptance ? "Yes" : "No"}
              />
              <Row label="Instructions" value={job.instructions} />
            </SectionGroup>
          </div>
        </div>

        <div className="lg:col-span-6 space-y-5">
          <div className="bg-white shadow rounded-lg p-6 space-y-6">
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <h3 className="text-xl font-bold text-gray-900">
                Job Assignment & Status
              </h3>

              <div className="flex gap-2 flex-wrap">
                {job.status === "Offered to Vendor" && (
                  <>
                    <button
                      onClick={handleAcceptOffer}
                      disabled={actionLoading}
                      className={btn("success", "sm")}
                    >
                      Accept Offer
                    </button>
                    <button
                      onClick={handleRejectOffer}
                      disabled={actionLoading}
                      className={btn("danger", "sm")}
                    >
                      Reject Offer
                    </button>
                  </>
                )}
                {job.status === "Started" &&
                  job.outputFiles?.some((f) => f.vendorUploader) && (
                    <button
                      onClick={handleCompleteJob}
                      disabled={actionLoading}
                      className={btn("success", "sm")}
                    >
                      Complete Job
                    </button>
                  )}
              </div>
            </div>

            {job.status === "Offered to Vendor" && (
              <div className="flex gap-3 rounded-md border border-purple-200 bg-purple-50 p-4 text-sm">
                <div className="font-semibold text-purple-700">
                  Action required
                </div>
                <div className="text-purple-700">
                  You have a pending job offer. Please accept or reject.
                </div>
              </div>
            )}

            {job.status === "Started" &&
              job.outputFiles?.some((f) => f.vendorUploader) && (
                <div className="flex gap-3 rounded-md border border-green-200 bg-green-50 p-4 text-sm">
                  <div className="font-semibold text-green-700">
                    Ready to complete
                  </div>
                  <div className="text-green-700">
                    You have uploaded output files. You can now mark this job as
                    completed.
                  </div>
                </div>
              )}

            <div className="border border-gray-200 rounded-md overflow-hidden">
              <Row label="Project" value={job.project?.project_name} />
              <Row label="Primary project manager" value={primaryManager} />
              <Row label="Status">
                <span
                  className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${statusCls}`}
                >
                  {job.status}
                </span>
              </Row>
              <Row label="Deadline" value={deadlineDisplay()} />
              <Row label="Started at" value={fmtDate(job.started_at)} />
              <Row label="Completed at" value={fmtDate(job.completed_at)} />
            </div>
          </div>
        </div>
      </div>

      {financesVisible && <ReceivablesCard data={job.receivables || []} />}

      <InputFilesCard
        files={job.inputFiles || []}
        downloading={inputDownloading}
        onDownloadZip={handleDownloadInputZip}
        onDownloadSingle={handleDownloadSingle}
      />

      <OutputFilesCard
        files={job.outputFiles || []}
        downloading={outputDownloading}
        onDownloadZip={handleDownloadOutputZip}
        onDownloadSingle={handleDownloadSingle}
        onUpload={() => setShowUploadModal(true)}
        status={job.status}
      />

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">Messages</h3>
        </div>
        <div className="px-5 py-4 space-y-3">
          <textarea
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write a message..."
            className="w-full border border-gray-300 rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <div className="flex justify-end">
            <button
              disabled={!message.trim()}
              className={btn("primary", "sm")}
            >
              Send
            </button>
          </div>
        </div>
      </div>

      {showUploadModal && (
        <VendorUploadOutputFileModal
          jobId={jobId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            fetchJob();
          }}
        />
      )}
    </div>
  );
};

export default VendorJobDetailPage;
